import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { usePractice } from '../contexts/PracticeContext';
import { useSocket } from '../contexts/SocketContext';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { Users, Send, Check, CheckCheck } from 'lucide-react';
import { toast } from 'sonner';
import { playSoundForAlert } from '../lib/sounds';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const DENTAL_PULSE_LOGO = 'https://customer-assets.emergentagent.com/job_dentalpulse-4/artifacts/3espo6gq_DentalPulse_logoFinal%20%2B%20white.png';


const formatRole = (role) => {
  if (!role) return '';
  return role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Helper to format read receipt time
function formatReadTime(isoString) {
  if (!isoString) return '';
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export default function MessagingPage() {
  const auth = useAuth();
  const { darkMode } = useTheme();
  const { practice } = usePractice();
  const { socket } = useSocket();
  const [contacts, setContacts] = useState({ groups: [], individuals: [] });
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const endRef = useRef(null);
  const previousMessageIds = useRef(new Set());

  useEffect(function() {
    axios.get(API + '/messages/contacts')
      .then(function(res) { setContacts(res.data); setLoading(false); })
      .catch(function() { toast.error('Failed'); setLoading(false); });
  }, []);

  useEffect(function() {
    if (!selected) return;
    function load() {
      axios.get(API + '/messages/conversation/' + selected.type + '/' + selected.id)
        .then(function(res) { 
          const newMessages = res.data.messages;
          
          // Check for new incoming messages (not from current user)
          newMessages.forEach(function(msg) {
            if (!previousMessageIds.current.has(msg.id) && msg.sender_id !== auth.user?.id) {
              // Play sound for new incoming message
              const soundSettings = {
                sounds_enabled: practice?.sounds_enabled !== false,
                sound_volume: practice?.sound_volume ?? 0.7,
                sound_assignments: practice?.sound_assignments || {}
              };
              playSoundForAlert('message', soundSettings);
            }
          });
          
          // Update previous message IDs
          previousMessageIds.current = new Set(newMessages.map(function(m) { return m.id; }));
          setMessages(newMessages);
        });
    }
    load();
    if (socket) {
      const handleNewMessage = () => load();
      socket.on('new_message', handleNewMessage);
      var iv = setInterval(load, 30000);
      return function() {
        socket.off('new_message', handleNewMessage);
        clearInterval(iv);
      };
    }
    var iv = setInterval(load, 5000);
    return function() { clearInterval(iv); };
  }, [selected, auth.user?.id, practice, socket]);

  useEffect(function() {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  function send(e) {
    e.preventDefault();
    if (!text.trim() || !selected) return;
    axios.post(API + '/messages/send', {
      recipient_type: selected.type,
      recipient_id: selected.id,
      content: text.trim()
    }).then(function() {
      setText('');
      return axios.get(API + '/messages/conversation/' + selected.type + '/' + selected.id);
    }).then(function(res) { setMessages(res.data.messages); });
  }

  // Apply custom background from practice settings
  const bgStyle = darkMode ? {
    backgroundColor: '#0f172a',
    backgroundImage: practice?.background_image_dark ? `url(${practice.background_image_dark})` : 'none',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed'
  } : {
    backgroundColor: practice?.background_color || '#f8fafc',
    backgroundImage: practice?.background_image ? `url(${practice.background_image})` : 'none',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed'
  };

  if (loading) return (
    <div className={`min-h-screen flex items-center justify-center`} style={bgStyle}>
      <div className={darkMode ? 'text-slate-300' : 'text-slate-600'}>Loading...</div>
    </div>
  );

  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''}`} style={bgStyle}>
      <Sidebar />
      <div className="pl-28 pr-8 pt-6 pb-8 h-screen flex flex-col">
        <div className={`glass-panel rounded-2xl p-4 mb-4 flex items-center justify-between ${darkMode ? 'bg-slate-800/50' : ''}`}>
          <h1 className={`text-2xl font-bold ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>Messages</h1>
          <div className="flex-shrink-0 ml-4">
            <img 
              src={DENTAL_PULSE_LOGO} 
              alt="DentalPulse"
              className="h-10 w-auto object-contain"
              data-testid="dentalpulse-logo"
            />
          </div>
        </div>
        <div className={`flex-1 flex rounded-2xl overflow-hidden border ${darkMode ? 'border-slate-700 bg-slate-800/50' : 'glass-panel'}`}>
          {/* Contacts sidebar */}
          <div className={`w-1/3 border-r flex flex-col ${darkMode ? 'border-slate-700 bg-slate-800/80' : 'bg-white/40'}`}>
            <div className={`p-4 border-b ${darkMode ? 'border-slate-700' : ''}`}>
              <h3 className={`text-xs font-semibold uppercase mb-3 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Groups</h3>
              {contacts.groups.map(function(g) {
                var sel = selected && selected.id === g.id;
                return (
                  <button key={g.id} onClick={function() { setSelected({ id: g.id, name: g.name, type: 'group', count: g.member_count }); }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl mb-1 transition-colors ${
                      sel 
                        ? 'bg-dp-600 text-white' 
                        : darkMode 
                          ? 'hover:bg-slate-700 text-slate-200' 
                          : 'hover:bg-slate-100 text-slate-800'
                    }`}>
                    <Users className="w-5 h-5" />
                    <span className="flex-1 text-left">{g.name}</span>
                    <span className="text-xs">{g.member_count}</span>
                  </button>
                );
              })}
            </div>
            <div className={`flex-1 overflow-y-auto p-4 ${darkMode ? 'dark-scrollbar' : 'light-scrollbar'}`}>
              <h3 className={`text-xs font-semibold uppercase mb-3 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Team Members</h3>
              {contacts.individuals.map(function(c) {
                var sel = selected && selected.id === c.id;
                return (
                  <button key={c.id} onClick={function() { setSelected({ id: c.id, name: c.name, type: 'individual', role: c.base_role || c.role, profile_picture: c.profile_picture }); }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl mb-1 transition-colors ${
                      sel 
                        ? 'bg-dp-600 text-white' 
                        : darkMode 
                          ? 'hover:bg-slate-700 text-slate-200' 
                          : 'hover:bg-slate-100 text-slate-800'
                    }`}>
                    {c.profile_picture ? (
                      <img src={c.profile_picture} alt={c.name} className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${darkMode ? 'bg-slate-600' : 'bg-slate-200'}`}>
                        <span>{c.name.charAt(0)}</span>
                      </div>
                    )}
                    <div className="flex-1 text-left">
                      <div className="font-medium">{c.name}</div>
                      <div className={`text-xs ${sel ? 'text-dp-100' : darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{formatRole(c.base_role || c.role)}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          {/* Chat area */}
          <div className={`w-2/3 flex flex-col ${darkMode ? 'bg-slate-800/50' : 'bg-white/60'}`}>
            {selected ? (
              <>
                <div className={`px-6 py-4 border-b ${darkMode ? 'border-slate-700 bg-slate-800/80' : 'bg-white/80'}`}>
                  <h2 className={`font-bold ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>{selected.name}</h2>
                  <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{selected.type === 'group' ? selected.count + ' members' : formatRole(selected.role)}</p>
                </div>
                <div className={`flex-1 overflow-y-auto p-6 space-y-4 ${darkMode ? 'dark-scrollbar' : 'light-scrollbar'}`}>
                  {messages.map(function(m) {
                    var mine = auth.user && m.sender_id === auth.user.id;
                    // For individual chats: check if recipient has read it
                    // For group chats: check how many have read it
                    var isRead = false;
                    var readTime = null;
                    var readCount = 0;
                    
                    if (mine && m.read_by) {
                      if (selected.type === 'individual') {
                        // For individual: check if the other person has read it
                        isRead = m.read_by.includes(selected.id);
                        if (isRead && m.read_at && m.read_at[selected.id]) {
                          readTime = m.read_at[selected.id];
                        }
                      } else {
                        // For groups: count readers (excluding self)
                        readCount = m.read_by.filter(function(id) { return id !== auth.user.id; }).length;
                        isRead = readCount > 0;
                      }
                    }
                    
                    return (
                      <div key={m.id} className={'flex ' + (mine ? 'justify-end' : 'justify-start')}>
                        <div className="max-w-[70%]">
                          {!mine && <p className={`text-xs mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{m.sender_name}</p>}
                          <div className={`px-4 py-2.5 rounded-2xl ${
                            mine 
                              ? 'bg-dp-600 text-white' 
                              : darkMode 
                                ? 'bg-slate-700 text-slate-100' 
                                : 'bg-white shadow-sm text-slate-800'
                          }`}>
                            <p>{m.content}</p>
                          </div>
                          {/* Read receipt for sender's messages */}
                          {mine && (
                            <div className={`flex items-center justify-end gap-1 mt-1 text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                              {selected.type === 'individual' ? (
                                // Individual chat: show single/double checkmark
                                <>
                                  {isRead ? (
                                    <CheckCheck className={`w-3.5 h-3.5 ${darkMode ? 'text-dp-400' : 'text-dp-500'}`} />
                                  ) : (
                                    <Check className="w-3.5 h-3.5" />
                                  )}
                                  {isRead && readTime && (
                                    <span className={darkMode ? 'text-dp-400' : 'text-dp-500'}>
                                      Read {formatReadTime(readTime)}
                                    </span>
                                  )}
                                </>
                              ) : (
                                // Group chat: show read count
                                <>
                                  {isRead ? (
                                    <CheckCheck className={`w-3.5 h-3.5 ${darkMode ? 'text-dp-400' : 'text-dp-500'}`} />
                                  ) : (
                                    <Check className="w-3.5 h-3.5" />
                                  )}
                                  {readCount > 0 && (
                                    <span className={darkMode ? 'text-dp-400' : 'text-dp-500'}>
                                      Read by {readCount}
                                    </span>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <div ref={endRef} />
                </div>
                <div className={`p-4 border-t ${darkMode ? 'border-slate-700 bg-slate-800/80' : 'bg-white/80'}`}>
                  <form onSubmit={send} className="flex gap-3">
                    <textarea value={text} onChange={function(e) { setText(e.target.value); }} placeholder="Type a message..."
                      className={`flex-1 px-4 py-3 rounded-xl border resize-none ${
                        darkMode 
                          ? 'bg-slate-700 border-slate-600 text-slate-100 placeholder-slate-400' 
                          : 'bg-white border-slate-200 text-slate-800'
                      }`} rows={2} />
                    <Button type="submit" disabled={!text.trim()} className="px-6 bg-dp-600 text-white rounded-xl hover:bg-dp-700">
                      <Send className="w-5 h-5" />
                    </Button>
                  </form>
                </div>
              </>
            ) : (
              <div className={`flex-1 flex items-center justify-center ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                <div className="text-center">
                  <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>Select a contact to start messaging</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
